'use client';

import { useState, useRef } from 'react';
import { Download, Printer, Plus, Trash2, Save, FileText, User, Mail, MapPin, Phone, CreditCard, Calendar, Hash, DollarSign, FileEdit, RefreshCw, X, Globe } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-hot-toast';
import { useReactToPrint } from 'react-to-print';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { invoiceGeneratorContent } from '../../data/invoice-generator-content';
import Link from 'next/link';

type LineItem = {
    id: string;
    description: string;
    quantity: number;
    rate: number;
    amount: number;
};

type BusinessInfo = {
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    country: string;
    taxId: string;
    website: string;
    logo?: string;
};

type ClientInfo = {
    name: string;
    email: string;
    phone: string;
    address: string;
};

type InvoiceData = {
    invoiceNumber: string;
    issueDate: string;
    dueDate: string;
    status: 'draft' | 'sent' | 'paid' | 'overdue';
    notes: string;
    terms: string;
    taxRate: number;
    discount: number;
    currency: string;
    items: LineItem[];
    business: BusinessInfo;
    client: ClientInfo;
};

export default function InvoiceGenerator() {
    // Invoice state
    const [invoice, setInvoice] = useState<InvoiceData>(() => ({
        invoiceNumber: `INV-${Math.floor(1000 + Math.random() * 9000)}`,
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'draft',
        notes: 'Thank you for your business!',
        terms: 'Payment due within 30 days',
        taxRate: 10,
        discount: 0,
        currency: 'USD',
        items: [
            {
                id: uuidv4(),
                description: 'Web Development Services',
                quantity: 1,
                rate: 100,
                amount: 100,
            },
        ],
        business: {
            name: 'Your Business Name',
            email: 'billing@yourbusiness.com',
            phone: '+1 (555) 123-4567',
            address: '123 Business St',
            city: 'San Francisco, CA 94103',
            country: 'United States',
            taxId: '12-3456789',
            website: 'www.yourbusiness.com',
        },
        client: {
            name: 'Client Name',
            email: 'client@example.com',
            phone: '+1 (555) 987-6543',
            address: '456 Client Ave\nNew York, NY 10001\nUnited States',
        },
    }));

    // Refs
    const invoiceRef = useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = useState('details');
    const [isSaving, setIsSaving] = useState(false);

    // Calculate subtotal, tax, and total
    const subtotal = invoice.items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    const taxAmount = (subtotal * invoice.taxRate) / 100;
    const discountAmount = (subtotal * invoice.discount) / 100;
    const total = subtotal + taxAmount - discountAmount;

    // Handle line item changes
    const handleItemChange = (id: string, field: keyof LineItem, value: string | number) => {
        setInvoice(prev => ({
            ...prev,
            items: prev.items.map(item => {
                if (item.id === id) {
                    const updatedItem = { ...item, [field]: value };
                    // Recalculate amount if quantity or rate changes
                    if (field === 'quantity' || field === 'rate') {
                        updatedItem.amount = Number(updatedItem.quantity) * Number(updatedItem.rate);
                    }
                    return updatedItem;
                }
                return item;
            }),
        }));
    };

    // Add new line item
    const addNewItem = () => {
        setInvoice(prev => ({
            ...prev,
            items: [
                ...prev.items,
                {
                    id: uuidv4(),
                    description: '',
                    quantity: 1,
                    rate: 0,
                    amount: 0,
                },
            ],
        }));
    };

    // Remove line item
    const removeItem = (id: string) => {
        if (invoice.items.length > 1) {
            setInvoice(prev => ({
                ...prev,
                items: prev.items.filter(item => item.id !== id),
            }));
        } else {
            toast.error('At least one item is required');
        }
    };

    // Handle business info changes
    const handleBusinessChange = (field: keyof BusinessInfo, value: string) => {
        setInvoice(prev => ({
            ...prev,
            business: {
                ...prev.business,
                [field]: value,
            },
        }));
    };

    // Handle client info changes
    const handleClientChange = (field: keyof ClientInfo, value: string) => {
        setInvoice(prev => ({
            ...prev,
            client: {
                ...prev.client,
                [field]: value,
            },
        }));
    };

    // Handle file upload for logo
    const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                handleBusinessChange('logo', reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // Generate a new invoice number
    const generateNewInvoiceNumber = () => {
        const newNumber = `INV-${Math.floor(1000 + Math.random() * 9000)}`;
        setInvoice(prev => ({
            ...prev,
            invoiceNumber: newNumber,
        }));
        toast.success('New invoice number generated');
    };

    // Save invoice to local storage
    const saveInvoice = () => {
        try {
            setIsSaving(true);
            const invoices = JSON.parse(localStorage.getItem('invoices') || '[]');
            const existingIndex = invoices.findIndex((inv: InvoiceData) => inv.invoiceNumber === invoice.invoiceNumber);

            if (existingIndex >= 0) {
                invoices[existingIndex] = invoice;
            } else {
                invoices.push(invoice);
            }

            localStorage.setItem('invoices', JSON.stringify(invoices));
            toast.success('Invoice saved successfully');
        } catch (error) {
            console.error('Error saving invoice:', error);
            toast.error('Failed to save invoice');
        } finally {
            setIsSaving(false);
        }
    };

    // Reset form
    const resetForm = () => {
        if (confirm('Are you sure you want to create a new invoice? All unsaved changes will be lost.')) {
            setInvoice({
                ...invoice,
                invoiceNumber: `INV-${Math.floor(1000 + Math.random() * 9000)}`,
                issueDate: new Date().toISOString().split('T')[0],
                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                status: 'draft',
                items: [
                    {
                        id: uuidv4(),
                        description: '',
                        quantity: 1,
                        rate: 0,
                        amount: 0,
                    },
                ],
                client: {
                    name: '',
                    email: '',
                    phone: '',
                    address: '',
                },
            });
            toast.success('New invoice created');
        }
    };

    // Handle print
    const handlePrint = useReactToPrint({
        documentTitle: `Invoice_${invoice.invoiceNumber}`,
        pageStyle: `
        @page { margin: 0.5in; }
        @media print {
            body { -webkit-print-color-adjust: exact; }
            .no-print { display: none !important; }
            .print-only { display: block !important; }
            .print-border { border: 1px solid #e5e7eb !important; }
        }
    `,
        content: () => invoiceRef.current as unknown as HTMLElement,
        removeAfterPrint: true,
        onAfterPrint: () => console.log('Print finished')
    } as any);

    // Make sure to pass the ref to the root element of your invoice component
    // Example: <div ref={invoiceRef}>
    // ... rest of your invoice JSX
    // </div>

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: invoice.currency,
        }).format(amount);
    };

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-6">
            <div className="mb-8">
                <ToolPageIntro title="Invoice Generator">
                Create professional invoices instantly with our free online invoice generator. Generate PDF invoices with customizable templates for your business.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Toolbar */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => setActiveTab('details')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeTab === 'details' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            <span className="flex items-center">
                                <FileText className="h-4 w-4 mr-1.5" />
                                Invoice Details
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('business')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeTab === 'business' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            <span className="flex items-center">
                                <User className="h-4 w-4 mr-1.5" />
                                Business Info
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('client')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md ${activeTab === 'client' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:bg-gray-100'
                                }`}
                        >
                            <span className="flex items-center">
                                <User className="h-4 w-4 mr-1.5" />
                                Client Info
                            </span>
                        </button>
                    </div>

                    <div className="flex items-center space-x-2">
                        <button
                            onClick={saveInvoice}
                            disabled={isSaving}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center disabled:opacity-50"
                        >
                            <Save className="h-4 w-4 mr-1.5" />
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
                        >
                            <Printer className="h-4 w-4 mr-1.5" />
                            Print/PDF
                        </button>
                        <button
                            onClick={resetForm}
                            className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                        >
                            <FileEdit className="h-4 w-4 mr-1.5" />
                            New Invoice
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {activeTab === 'details' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                                    <div className="flex rounded-md shadow-sm">
                                        <input
                                            type="text"
                                            value={invoice.invoiceNumber}
                                            onChange={(e) => setInvoice({ ...invoice, invoiceNumber: e.target.value })}
                                            className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-l-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        />
                                        <button
                                            onClick={generateNewInvoiceNumber}
                                            className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100"
                                            title="Generate new invoice number"
                                        >
                                            <RefreshCw className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Calendar className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            value={invoice.issueDate}
                                            onChange={(e) => setInvoice({ ...invoice, issueDate: e.target.value })}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Calendar className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="date"
                                            value={invoice.dueDate}
                                            onChange={(e) => setInvoice({ ...invoice, dueDate: e.target.value })}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-gray-200 pt-4">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-lg font-medium text-gray-900">Items</h3>
                                    <button
                                        type="button"
                                        onClick={addNewItem}
                                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Item
                                    </button>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Description
                                                </th>
                                                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Qty
                                                </th>
                                                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Rate
                                                </th>
                                                <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Amount
                                                </th>
                                                <th scope="col" className="relative px-3 py-3">
                                                    <span className="sr-only">Actions</span>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {invoice.items.map((item) => (
                                                <tr key={item.id}>
                                                    <td className="px-3 py-3 whitespace-nowrap">
                                                        <input
                                                            type="text"
                                                            value={item.description}
                                                            onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                                                            className="block w-full border-0 p-0 focus:ring-0 sm:text-sm"
                                                            placeholder="Item description"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-3 whitespace-nowrap">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={item.quantity}
                                                            onChange={(e) => handleItemChange(item.id, 'quantity', parseInt(e.target.value) || 0)}
                                                            className="block w-20 text-right border-0 p-0 focus:ring-0 sm:text-sm"
                                                        />
                                                    </td>
                                                    <td className="px-3 py-3 whitespace-nowrap">
                                                        <div className="flex items-center">
                                                            <span className="text-gray-500 text-sm mr-1">{invoice.currency}</span>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={item.rate}
                                                                onChange={(e) => handleItemChange(item.id, 'rate', parseFloat(e.target.value) || 0)}
                                                                className="block w-24 text-right border-0 p-0 focus:ring-0 sm:text-sm"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                                                        {formatCurrency(item.amount)}
                                                    </td>
                                                    <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                                                        <button
                                                            type="button"
                                                            onClick={() => removeItem(item.id)}
                                                            className="text-red-600 hover:text-red-900"
                                                            title="Remove item"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="mt-6 flex justify-end">
                                    <div className="w-full max-w-md">
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <span className="text-sm text-gray-600">Subtotal:</span>
                                                <span className="text-sm font-medium">{formatCurrency(subtotal)}</span>
                                            </div>

                                            <div className="flex justify-between">
                                                <div className="flex items-center">
                                                    <span className="text-sm text-gray-600">Tax ({invoice.taxRate}%):</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={invoice.taxRate}
                                                        onChange={(e) => setInvoice({ ...invoice, taxRate: parseFloat(e.target.value) || 0 })}
                                                        className="ml-2 w-16 border-0 border-b border-gray-300 p-0 text-sm focus:ring-0 focus:border-blue-500"
                                                    />
                                                </div>
                                                <span className="text-sm font-medium">{formatCurrency(taxAmount)}</span>
                                            </div>

                                            <div className="flex justify-between">
                                                <div className="flex items-center">
                                                    <span className="text-sm text-gray-600">Discount:</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={invoice.discount}
                                                        onChange={(e) => setInvoice({ ...invoice, discount: parseFloat(e.target.value) || 0 })}
                                                        className="ml-2 w-16 border-0 border-b border-gray-300 p-0 text-sm focus:ring-0 focus:border-blue-500"
                                                    />
                                                    <span className="ml-1 text-sm text-gray-500">%</span>
                                                </div>
                                                <span className="text-sm font-medium text-red-600">-{formatCurrency(discountAmount)}</span>
                                            </div>

                                            <div className="border-t border-gray-200 pt-2 flex justify-between">
                                                <span className="text-base font-medium text-gray-900">Total:</span>
                                                <span className="text-lg font-bold">{formatCurrency(total)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                        <textarea
                                            rows={3}
                                            value={invoice.notes}
                                            onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
                                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                                            placeholder="Thank you for your business!"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions</label>
                                        <textarea
                                            rows={3}
                                            value={invoice.terms}
                                            onChange={(e) => setInvoice({ ...invoice, terms: e.target.value })}
                                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border border-gray-300 rounded-md"
                                            placeholder="Payment due within 30 days"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'business' && (
                        <div className="space-y-6">
                            <div className="flex flex-col items-center mb-6">
                                <div className="relative w-32 h-32 mb-4">
                                    {invoice.business.logo ? (
                                        <>
                                            <img
                                                src={invoice.business.logo}
                                                alt="Business Logo"
                                                className="w-full h-full object-contain rounded-lg border border-gray-200"
                                            />
                                            <button
                                                onClick={() => handleBusinessChange('logo', '')}
                                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                title="Remove logo"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                                            <span className="text-sm text-gray-400">No logo</span>
                                        </div>
                                    )}
                                </div>
                                <label className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                    {invoice.business.logo ? 'Change Logo' : 'Upload Logo'}
                                    <input type="file" className="sr-only" accept="image/*" onChange={handleLogoUpload} />
                                </label>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={invoice.business.name}
                                            onChange={(e) => handleBusinessChange('name', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="Your Business Name"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            value={invoice.business.email}
                                            onChange={(e) => handleBusinessChange('email', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="billing@yourbusiness.com"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Phone className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="tel"
                                            value={invoice.business.phone}
                                            onChange={(e) => handleBusinessChange('phone', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="+1 (555) 123-4567"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Hash className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={invoice.business.taxId}
                                            onChange={(e) => handleBusinessChange('taxId', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="12-3456789"
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 pt-2">
                                            <MapPin className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <textarea
                                            rows={2}
                                            value={invoice.business.address}
                                            onChange={(e) => handleBusinessChange('address', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="123 Business St"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">City, State, ZIP</label>
                                    <input
                                        type="text"
                                        value={invoice.business.city}
                                        onChange={(e) => handleBusinessChange('city', e.target.value)}
                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="San Francisco, CA 94103"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                    <input
                                        type="text"
                                        value={invoice.business.country}
                                        onChange={(e) => handleBusinessChange('country', e.target.value)}
                                        className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                        placeholder="United States"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Globe className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="url"
                                            value={invoice.business.website}
                                            onChange={(e) => handleBusinessChange('website', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="www.yourbusiness.com"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'client' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <User className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            value={invoice.client.name}
                                            onChange={(e) => handleClientChange('name', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="Client Name"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Mail className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="email"
                                            value={invoice.client.email}
                                            onChange={(e) => handleClientChange('email', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="client@example.com"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Phone className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <input
                                            type="tel"
                                            value={invoice.client.phone}
                                            onChange={(e) => handleClientChange('phone', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="+1 (555) 987-6543"
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Billing Address</label>
                                    <div className="relative rounded-md shadow-sm">
                                        <div className="absolute inset-y-0 left-0 pl-3 pt-2">
                                            <MapPin className="h-4 w-4 text-gray-400" />
                                        </div>
                                        <textarea
                                            rows={3}
                                            value={invoice.client.address}
                                            onChange={(e) => handleClientChange('address', e.target.value)}
                                            className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                            placeholder="123 Client St\nNew York, NY 10001\nUnited States"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                                        <select
                                            value={invoice.currency}
                                            onChange={(e) => setInvoice({ ...invoice, currency: e.target.value })}
                                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                                        >
                                            <option value="USD">USD ($)</option>
                                            <option value="EUR">EUR (€)</option>
                                            <option value="GBP">GBP (£)</option>
                                            <option value="JPY">JPY (¥)</option>
                                            <option value="CAD">CAD (C$)</option>
                                            <option value="AUD">AUD (A$)</option>
                                            <option value="INR">INR (₹)</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            value={invoice.status}
                                            onChange={(e) => setInvoice({ ...invoice, status: e.target.value as any })}
                                            className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md ${invoice.status === 'draft' ? 'border-yellow-300 bg-yellow-50' :
                                                invoice.status === 'sent' ? 'border-blue-300 bg-blue-50' :
                                                    invoice.status === 'paid' ? 'border-green-300 bg-green-50' :
                                                        'border-red-300 bg-red-50'
                                                }`}
                                        >
                                            <option value="draft" className="bg-yellow-50">Draft</option>
                                            <option value="sent" className="bg-blue-50">Sent</option>
                                            <option value="paid" className="bg-green-50">Paid</option>
                                            <option value="overdue" className="bg-red-50">Overdue</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Invoice Preview */}
            <div className="mt-12">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900">Invoice Preview</h2>
                    <button
                        onClick={handlePrint}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <Printer className="h-4 w-4 mr-2" />
                        Print/Download
                    </button>
                </div>

                <div ref={invoiceRef} className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
                    {/* Invoice Header */}
                    <div className="flex flex-col md:flex-row justify-between mb-8">
                        <div className="mb-6 md:mb-0">
                            {invoice.business.logo ? (
                                <img src={invoice.business.logo} alt={invoice.business.name} className="h-12 mb-4" />
                            ) : (
                                <h1 className="text-2xl font-bold text-gray-900">{invoice.business.name}</h1>
                            )}
                            <div className="mt-2 text-sm text-gray-600 space-y-1">
                                <div>{invoice.business.address}</div>
                                <div>{invoice.business.city}</div>
                                <div>{invoice.business.country}</div>
                                {invoice.business.phone && <div>{invoice.business.phone}</div>}
                                {invoice.business.email && <div>{invoice.business.email}</div>}
                                {invoice.business.website && <div>{invoice.business.website}</div>}
                                {invoice.business.taxId && <div>Tax ID: {invoice.business.taxId}</div>}
                            </div>
                        </div>

                        <div className="text-right">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">INVOICE</h2>
                            <div className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 mb-4">
                                {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                            </div>
                            <div className="space-y-1 text-sm text-gray-600">
                                <div className="flex justify-between">
                                    <span className="text-gray-500 w-24">Invoice #:</span>
                                    <span className="font-medium">{invoice.invoiceNumber}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500 w-24">Issue Date:</span>
                                    <span>{new Date(invoice.issueDate).toLocaleDateString()}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500 w-24">Due Date:</span>
                                    <span className={new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid' ? 'text-red-600 font-medium' : ''}>
                                        {new Date(invoice.dueDate).toLocaleDateString()}
                                        {new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid' && ' (Overdue)'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bill To */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        <div>
                            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Bill To</h3>
                            <div className="bg-gray-50 p-4 rounded-md">
                                <div className="font-medium text-gray-900">{invoice.client.name}</div>
                                {invoice.client.email && <div className="text-sm text-gray-600">{invoice.client.email}</div>}
                                {invoice.client.phone && <div className="text-sm text-gray-600">{invoice.client.phone}</div>}
                                {invoice.client.address && (
                                    <div className="mt-2 text-sm text-gray-600 whitespace-pre-line">
                                        {invoice.client.address}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Payment Method</h3>
                            <div className="bg-gray-50 p-4 rounded-md">
                                <div className="flex items-center">
                                    <CreditCard className="h-5 w-5 text-gray-400 mr-2" />
                                    <span className="font-medium">Bank Transfer</span>
                                </div>
                                <div className="mt-2 text-sm text-gray-600">
                                    <div>Account Name: {invoice.business.name}</div>
                                    <div>Bank: Example Bank</div>
                                    <div>Account #: 1234 5678 9012 3456</div>
                                    <div>SWIFT/BIC: EXMPUS33</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invoice Items */}
                    <div className="mb-8">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Qty
                                        </th>
                                        <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Rate
                                        </th>
                                        <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {invoice.items.map((item, index) => (
                                        <tr key={index}>
                                            <td className="px-3 py-3 whitespace-normal text-sm text-gray-900">
                                                {item.description || 'Item description'}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                                                {item.quantity}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-right text-sm text-gray-500">
                                                {formatCurrency(item.rate)}
                                            </td>
                                            <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                                                {formatCurrency(item.amount)}
                                            </td>
                                        </tr>
                                    ))}

                                    {/* Subtotal */}
                                    <tr>
                                        <td colSpan={3} className="px-3 py-3 text-right text-sm font-medium text-gray-500">
                                            Subtotal
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                                            {formatCurrency(subtotal)}
                                        </td>
                                    </tr>

                                    {/* Tax */}
                                    {invoice.taxRate > 0 && (
                                        <tr>
                                            <td colSpan={3} className="px-3 py-1 text-right text-sm text-gray-500">
                                                Tax ({invoice.taxRate}%)
                                            </td>
                                            <td className="px-3 py-1 whitespace-nowrap text-right text-sm text-gray-900">
                                                {formatCurrency(taxAmount)}
                                            </td>
                                        </tr>
                                    )}

                                    {/* Discount */}
                                    {invoice.discount > 0 && (
                                        <tr>
                                            <td colSpan={3} className="px-3 py-1 text-right text-sm text-gray-500">
                                                Discount ({invoice.discount}%)
                                            </td>
                                            <td className="px-3 py-1 whitespace-nowrap text-right text-sm text-red-600">
                                                -{formatCurrency(discountAmount)}
                                            </td>
                                        </tr>
                                    )}

                                    {/* Total */}
                                    <tr className="border-t-2 border-gray-200">
                                        <td colSpan={3} className="px-3 py-3 text-right text-base font-bold text-gray-900">
                                            Total
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap text-right text-base font-bold text-gray-900">
                                            {formatCurrency(total)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Notes & Terms */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-12 pt-8 border-t border-gray-200">
                        {invoice.notes && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Notes</h3>
                                <div className="text-sm text-gray-600 whitespace-pre-line">
                                    {invoice.notes}
                                </div>
                            </div>
                        )}

                        {invoice.terms && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Terms & Conditions</h3>
                                <div className="text-sm text-gray-600 whitespace-pre-line">
                                    {invoice.terms}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
                        <div className="mb-2">
                            Thank you for your business!
                        </div>
                        <div>
                            {invoice.business.name} • {invoice.business.city} • {invoice.business.country}
                        </div>
                    </div>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={invoiceGeneratorContent.title}
                    description={invoiceGeneratorContent.description}
                    content={invoiceGeneratorContent.content}
                    faqs={invoiceGeneratorContent.faqs}
                    relatedTools={invoiceGeneratorContent.relatedTools}
                />
            </div>
        </div>
    );
}
